import React, { useEffect, useState, useMemo } from 'react';
import { Route, Switch } from 'react-router-dom';
import { useCommon } from '../../../contexts/CommonContext';
import DailyEngagedUsersContext from '../../../contexts/DailyEngagedUsersContext';
import { useTenant } from '../../../contexts/TenantContext';
import { useContentLocalizer } from '../../../hooks/useContentLocalizer';
import { localizationDailyEngagedUsers } from './localization';
import DailyEngagedUsersSummary from './components/DailyEngagedUsersSummary';
import MonthlyUsage from './components/Monthly/MonthlyUsage';
import DailyUsage from './components/Daily/DailyUsage';
import { DailyEngagedUsersMonthlyUsage } from '../../../models/billing/dailyEngagedUsers/DailyEngagedUsersMonthlyUsage';
import { sortByProperty } from '../../../utils/array';
import BillingDailyEngagedUsersServiceApi from '../../../api/Billing/BillingDailyEngagedUsersApi';
import { DailyEngagedUserUsage } from '../../../models/billing/dailyEngagedUsers/DailyEngagedUserUsage';
import { AsideMenu } from '../components/AsideMenu';
import { useBillingConsumptionContext } from '../../../contexts/Billing/BillingConsumptionContext';
import { action, origin, REPORT_DEU_CONSUMPTION_OPEN, REPORT_DEU_CONSUMPTION_CLOSED } from '../../../shared/SegmentEvents';
import { useBillingPlanUsageContext } from '../../../contexts/Billing/BillingPlanUsageContext';
import { useBillingMetricsContext } from '../../../contexts/Billing/BillingMetricsContext';
import { billingRoutes, billingSubRoutes } from '../../../routes/BillingRoutes';
import { usePageTracking } from '../hooks/usePageTracking';

const DailyEngagedUsers: React.FC = () => {
  const { dateToSearch, setDateToSearch, periodOptions, currentMonth } = useBillingConsumptionContext();
  const { isMetricAllowed } = useBillingMetricsContext();
  const {
    setHeaderContent,
    common: { loggedUser },
  } = useCommon();

  const { tenant } = useTenant();

  const PAGE_NAME = 'Consumo DEU';
  usePageTracking(PAGE_NAME, REPORT_DEU_CONSUMPTION_OPEN, REPORT_DEU_CONSUMPTION_CLOSED, tenant, loggedUser, true);

  const content = useContentLocalizer(localizationDailyEngagedUsers);
  const billingDailyEngagedUserApi = new BillingDailyEngagedUsersServiceApi();
  const [dailyEngagedUserMonthlyUsage, setDailyEngagedUserMonthlyUsage] = useState<DailyEngagedUsersMonthlyUsage[]>([]);
  const [dailyEngagedUserUsage, setDailyEngagedUserUsage] = useState<DailyEngagedUserUsage[]>([]);
  const { planStatus, getPlanStatus } = useBillingPlanUsageContext();

  useEffect(() => {
    setHeaderContent({
      redirect: '/data-consumption',
      text: content.formatString(content.header, tenant.info.name),
    });
    return () => setHeaderContent({ redirect: null });
  }, []);

  const sortDailyEngagedUserMonthlyUsage = (sort) => {
    const sortedChatbotsConsumption = sortByProperty(dailyEngagedUserMonthlyUsage, sort.property, sort.order);
    setDailyEngagedUserMonthlyUsage([...sortedChatbotsConsumption]);
  };

  const sortDailyEngagedUserUsage = (sort) => {
    const sortedChatbotsConsumption = sortByProperty(dailyEngagedUserUsage, sort.property, sort.order);
    setDailyEngagedUserUsage([...sortedChatbotsConsumption]);
  };

  const trackingProps = useMemo(() => ({
    date: dateToSearch,
    planStatus: planStatus.current,
    origin: origin.DEU,
    action: action.SELECT,
  }), [dateToSearch, planStatus.current]);

  const contextProps = useMemo(() => ({
    dateToSearch,
    setDateToSearch,
    periodOptions,
    currentMonth,
    content,
    tenant,
    loggedUser,
    setHeaderContent,
    billingDailyEngagedUserApi,
    dailyEngagedUserMonthlyUsage,
    dailyEngagedUserUsage,
    sortDailyEngagedUserMonthlyUsage,
    setDailyEngagedUserMonthlyUsage,
    sortDailyEngagedUserUsage,
    setDailyEngagedUserUsage,
    trackingProps,
    getPlanStatus,
    isMetricAllowed,
  }), [
    dateToSearch,
    setDateToSearch,
    periodOptions,
    currentMonth,
    content,
    tenant,
    loggedUser,
    setHeaderContent,
    billingDailyEngagedUserApi,
    dailyEngagedUserMonthlyUsage,
    dailyEngagedUserUsage,
    sortDailyEngagedUserMonthlyUsage,
    setDailyEngagedUserMonthlyUsage,
    sortDailyEngagedUserUsage,
    setDailyEngagedUserUsage,
    trackingProps,
    getPlanStatus,
    isMetricAllowed,
  ]);

  return (
    <DailyEngagedUsersContext.Provider value={contextProps}>
      <AsideMenu>
        <Switch>
          <Route exact path={billingRoutes.deuConsumption} component={DailyEngagedUsersSummary}></Route>
          <Route exact path={billingSubRoutes.deuConsumptionMonthly} component={MonthlyUsage}></Route>
          <Route exact path={billingSubRoutes.deuConsumptionDaily} component={DailyUsage}></Route>
        </Switch>
      </AsideMenu>
    </DailyEngagedUsersContext.Provider>
  );
};

export default DailyEngagedUsers;
